import React, { useEffect, useState } from "react";
import { TimerSection } from "../../../journey/Timer";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../../../../utils/consts/routes";
import { Button } from "../../../../atom/Button";
import { useTranslation } from "react-i18next";
import { Modal } from "../../../../atom/Modal";
import { TermsAndConditions } from "./Terms";
import { TimerProvider } from "../../../../../utils/contexts/TimerContext";
import { ArrowRight, EventSchedule, UserAvatarFilledAlt } from "@carbon/icons-react";
import { useUserContext } from "../../../../../utils/contexts/UserProvider";
import { postFetcher, putFetcher } from "../../../../../services/fetchers";
import { formatDateWithTime } from "../../../../../utils/date";
import { ToolTip } from "../../../../atom/Tooltip";
import { hasNotAcceptedAgreements } from "../../../../../utils/user";

interface ExamStartingCardProps {
  id?: string;
  examId?: string;
  title?: string;
  type?: string;
  questions?: number;
  date?: string;
  authorizedPerson?: string;
  startDate: string;
  is_completed?: boolean;
  is_finished?: boolean;
  endDate: string;
  isEnroll?: boolean;
  handleOnEnroll?: () => void;
  enrollDisabled?: boolean;
  examStatusCode?: string;
  secondsUntilStart: number;
  secondsUntilEnd: number;
}

export const StartingCard = ({
  id,
  examId,
  title,
  type,
  questions,
  authorizedPerson,
  startDate,
  is_completed,
  is_finished,
  endDate,
  isEnroll,
  handleOnEnroll,
  enrollDisabled,
  examStatusCode,
  secondsUntilStart,
  secondsUntilEnd,
}: ExamStartingCardProps) => {
  const navigate = useNavigate();
  const { user } = useUserContext();
  const [isStartExamModalOpen, setIsStartExamModalOpen] = useState(false);
  const [isEnrollExamModalOpen, setIsEnrollExamModalOpen] = useState(false);
  const [isTimerCompleted, setIsTimerCompleted] = useState(false);
  const { t } = useTranslation();
  const [isDisabled, setIsDisabled] = useState(true);

  const time = secondsUntilStart;
  const isDocumentationApproved = user?.is_documentation_approved;

  const [tooltipMessage, setTooltipMessage] = useState<string>("");
  const [tooltipVisible, setTooltipVisible] = useState<boolean>(false);

  const conditions = [
    {
      condition: !isTimerCompleted,
      message: t("Exam has not yet started."),
    },
    {
      condition: is_completed,
      message: t("Exam has been completed."),
    },
    {
      condition: is_finished,
      message: t("Exam has ended."),
    },
    {
      condition: !isDocumentationApproved,
      message: t("Your documentation has not been approved."),
    },
    {
      condition: enrollDisabled,
      message: t("Enrolling is disabled."),
    },
    {
      condition: hasNotAcceptedAgreements(user),
      message: t("Data agreement has not been accepted."),
    },
    {
      condition: !user?.is_gdpr_accepted,
      message: t("GDPR has not been accepted."),
    },
    {
      condition: examStatusCode !== "scheduled",
      message: t("Exam is not yet scheduled."),
    },
  ];

  useEffect(() => {
    const failedCondition = conditions?.find((item) => item.condition);
    setTooltipMessage(failedCondition?.message ?? "");

    if (
      isTimerCompleted &&
      !is_completed &&
      !is_finished &&
      isDocumentationApproved &&
      !enrollDisabled &&
      user?.is_agreement_accepted &&
      user?.is_gdpr_accepted &&
      examStatusCode === "scheduled"
    ) {
      setIsDisabled(false);
    }
  }, [isTimerCompleted, is_completed, is_finished]);

  const handleEnrollClick = async () => {
    setIsEnrollExamModalOpen(false);
    await postFetcher(
      `/common/participant-exam/`,
      {
        arg: {
          id: crypto.randomUUID(),
          exam: { id },
          participant: user,
        },
      },
      false,
      "participant",
    );
    handleOnEnroll?.();
  };

  return (
    <TimerProvider initialTime={time}>
      <div className="bg-white pt-5 rounded-lg shadow-lg">
        <div className="flex flex-col mb-4 px-6">
          <div className="flex justify-between items-center">
            <p className={`text-[24px] font-semibold leading-[30.24px]`}>{title}</p>
            <TimerSection
              startDate={startDate}
              setIsTimerCompleted={setIsTimerCompleted}
              rightIcon={<EventSchedule size={20} className={`text-xl text-orange-700`} />}
            />
          </div>
          <p className="text-sm text-gray-375">
            {formatDateWithTime(startDate, { excludeSeconds: true })} -{" "}
            {formatDateWithTime(endDate, { excludeSeconds: true })}
          </p>
        </div>
        <div className="px-6">
          <p className="text-[16px] font-normal leading-[20.16px] text-black ">
            {t(type as string)}
          </p>
          <p className="text-[16px] font-light leading-[20.16px] text-gray-375">
            ({questions} {t("questions")})
          </p>
        </div>
        <hr className="bg-gray-200 mt-8 mb-5" />
        <div className="px-6 pb-5">
          <div className="flex justify-between items-center">
            <div className="flex flex-col justify-start items-start gap-2">
              <p className="text-[13px] font-light leading-[16.38px] tracking-[0.2px] text-gray-375">
                {t("Authorized person")}:
              </p>
              <div className="flex flex-row justify-start items-center gap-1">
                <UserAvatarFilledAlt size={20} className="text-black" />
                <p className="text-black text-[16px] font-normal leading-[20.16px]">
                  {authorizedPerson}
                </p>
              </div>
            </div>
            {!isEnroll ? (
              <div
                className="relative"
                onMouseEnter={() => setTooltipVisible(true)}
                onMouseLeave={() => setTooltipVisible(false)}>
                <Button
                  isDisabled={isDisabled}
                  type="primary"
                  label={t("Start")}
                  onClick={() => setIsStartExamModalOpen(true)}
                  leftIcon={<ArrowRight size={24} />}
                />
                {tooltipVisible && tooltipMessage && (
                  <ToolTip text={tooltipMessage} type="nav" position="top-right" />
                )}
              </div>
            ) : (
              <Button
                isDisabled={enrollDisabled}
                label={t("Enroll")}
                leftIcon={<ArrowRight size={24} />}
                onClick={() => setIsEnrollExamModalOpen(true)}
                type="primary"
              />
            )}
          </div>
        </div>
        <Modal
          isModalOpen={isStartExamModalOpen}
          closeModal={() => setIsStartExamModalOpen(false)}
          secondButtonClick={() => {
            putFetcher(`/common/participant-exam`, {
              arg: {
                id,
                resourceId: id,
                is_rules_accepted: true,
                participant: user,
                exam: { id: examId },
              },
            });
            navigate(ROUTES.exam, {
              state: { examId: id, id: examId, secondsLeft: secondsUntilEnd },
            });
          }}
          title={t("Exam declarations")}
          subtitle={""}
          firstButtonLabel={""}
          secondButtonLabel={t("Confirm & Start Exam")}
          content={<TermsAndConditions />}
          additionalClassName="sm:max-w-[720px]"
        />
        <Modal
          isModalOpen={isEnrollExamModalOpen}
          closeModal={() => setIsEnrollExamModalOpen(false)}
          firstButtonClick={() => setIsEnrollExamModalOpen(false)}
          firstButtonLabel={t("Cancel")}
          secondButtonClick={handleEnrollClick}
          secondButtonLabel={t("Enroll")}
          secondButtonType="primary"
          title={t("Enroll?")}
          subtitle={t("Are you sure you want to enroll into this exam?")}
        />
      </div>
    </TimerProvider>
  );
};
